import React from 'react';
import {RouteComponentProps, Link} from 'react-router-dom';
import {Box, Typography, Breadcrumbs} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import ApplicationJourneyStepper from './Journey/ApplicationJourneyStepper';
import ApplicationDashboardStep from './Journey/ApplicationDashboardStep';

import ApplicationCreditCheckStep from './Journey/ApplicationCreditCheckStep';
import ApplicationFarmInformationStep from './Journey/ApplicationFarmInformationStep';
import ApplicationProductionInfoStep from './Journey/ApplicationProductionInfoStep';
import ApplicationFinancialsStep from './Journey/ApplicationFinancialsStep';
import ApplicationDocumentsStep from './Journey/ApplicationDocumentsStep';
import ApplicationAgreementStep from './Journey/ApplicationAgreementStep';
import ApplicationJourneyPlaceholderStep from './Journey/ApplicationJourneyPlaceholderStep';
import {APPLICATION_JOURNEY_STEPS} from './Journey/journeyConfig';
import {
  SupplierApplicationJourneyProvider,
  useSupplierApplicationJourney,
} from './Journey/SupplierApplicationJourneyContext';

import './ApplicationDetail.scss';
import CompanyDetailsStepContainer from './Journey/steps/company-details';

interface LocationState {
  origin?: string;
}

interface MatchParams {
  applicationId: string;
}

type ApplicationDetailProps = RouteComponentProps<
  MatchParams,
  any,
  LocationState
>;

function ApplicationDetailContent() {
  const {
    applicationId,
    origin,
    isLoading,
    errorMessage,
    application,
    completion,
    activeStep,
    activeSubStepIndex,
    hasPrevious,
    hasNext,
    companySummary,
    farmSummary,
    goToStep,
    goToSubStep,
    goPrevious,
    goNext,
  } = useSupplierApplicationJourney();
  const showInitialSkeleton = isLoading && !application;

  return (
    <Box className="application-detail-container application-journey-detail">
      <Breadcrumbs
        aria-label="breadcrumb"
        className="breadcrumb-row"
        separator=" / "
      >
        {origin === 'applications' ? (
          <Link
            to="/applications"
            style={{textDecoration: 'none', color: '#267373'}}
          >
            Applications
          </Link>
        ) : (
          <Link
            to="/customers"
            style={{textDecoration: 'none', color: '#267373'}}
          >
            Farmers
          </Link>
        )}
        <Typography color="textPrimary" style={{fontWeight: 700}}>
          {application?.id || applicationId}
        </Typography>
      </Breadcrumbs>

      {showInitialSkeleton ? (
        <Skeleton variant="rect" width="100%" height={520} />
      ) : (
        <Box className="funding-app-wrapper">
          <header className="application-header">
            <div className="application-header__titles">
              <p className="application-header__eyebrow">Funding journey</p>
              <h1>Complete your Khula Money application</h1>
              <p>
                Work through each section to tell us about your business, team
                and financials. Your progress is saved as you go.
              </p>
            </div>
            <div className="application-header__progress" aria-live="polite">
              <span className="application-header__progress-value">
                {completion}%
              </span>
              <span className="application-header__progress-label">
                complete
              </span>
              <div className="application-header__progress-bar">
                <span style={{width: `${completion}%`}} />
              </div>
            </div>
          </header>

          <div className="application-workspace">
            <aside
              className="application-sidebar"
              aria-label="Application navigation"
            >
              <section className="application-sidebar__panel">
                <header className="application-sidebar__panel-header">
                  <p className="application-sidebar__eyebrow">
                    Journey sections
                  </p>
                  <p className="application-sidebar__title">
                    {completion}% complete
                  </p>
                </header>
                <ApplicationJourneyStepper
                  steps={APPLICATION_JOURNEY_STEPS}
                  activeKey={activeStep.key}
                  onStepClick={(step) => goToStep(step.key)}
                  completion={completion}
                  variant="stack"
                />
              </section>
            </aside>

            <section className="application-stage">
              {errorMessage ? (
                <div className="journey-status-banner is-error">
                  {errorMessage}
                </div>
              ) : null}
              {activeStep.key === 'dashboard' ? (
                <ApplicationDashboardStep />
              ) : activeStep.key === 'company-details' ? (
                <CompanyDetailsStepContainer />
              ) : activeStep.key === 'credit-check' ? (
                <ApplicationCreditCheckStep />
              ) : activeStep.key === 'farm-information' ? (
                <ApplicationFarmInformationStep />
              ) : activeStep.key === 'production-info' ? (
                <ApplicationProductionInfoStep />
              ) : activeStep.key === 'financials' ? (
                <ApplicationFinancialsStep />
              ) : activeStep.key === 'upload-documents' ? (
                <ApplicationDocumentsStep />
              ) : activeStep.key === 'agreement' ? (
                <ApplicationAgreementStep />
              ) : (
                <ApplicationJourneyPlaceholderStep
                  step={activeStep}
                  activeSubStepIndex={activeSubStepIndex}
                  applicationId={application?.id}
                  farmName={farmSummary.name || 'Farm name pending'}
                  companyName={companySummary.displayName || 'Company pending'}
                  onPrevious={goPrevious}
                  onNext={goNext}
                  onSubStepClick={goToSubStep}
                  hasPrevious={hasPrevious}
                  hasNext={hasNext}
                />
              )}
            </section>
          </div>
        </Box>
      )}
    </Box>
  );
}

function ApplicationDetail(props: ApplicationDetailProps) {
  const origin = props.location.state?.origin || 'applications';

  return (
    <SupplierApplicationJourneyProvider
      applicationId={props.match.params.applicationId}
      origin={origin}
    >
      <ApplicationDetailContent />
    </SupplierApplicationJourneyProvider>
  );
}

export default ApplicationDetail;
